import { z } from 'zod';
import { ChannelId } from './common.js';

/**
 * One channel a person moderates on Twitch, and what that means on tomo.
 *
 *   active       mod tools are yours here.
 *   tools_off    the channel is on tomo, but its owner has not switched mod
 *                tools on. Only the owner can (ADR 0050 §4).
 *   suspended    the owner switched YOU off on tomo. Twitch still has you as
 *                a moderator; tomo does not act for you here.
 *   not_on_tomo  the broadcaster has not connected. Invite them.
 */
export const ModeratedChannel = z.object({
  login: z.string(),
  display_name: z.string(),
  /** The tomo channel, when the broadcaster has connected one. */
  channel_id: ChannelId.nullable(),
  state: z.enum(['active', 'tools_off', 'suspended', 'not_on_tomo']),
});
export type ModeratedChannel = z.infer<typeof ModeratedChannel>;

/**
 * The signed-in person's moderator standing, everywhere.
 *
 * `connected` false means they have not granted the moderator consent yet, and
 * `channels` is empty: tomo does not know what they moderate until Twitch says.
 */
export const Moderation = z.object({
  connected: z.boolean(),
  /** The moderator consent has fallen behind what tomo now asks for. Reconnect. */
  scopes_missing: z.array(z.string()),
  channels: z.array(ModeratedChannel),
});
export type Moderation = z.infer<typeof Moderation>;

/**
 * A moderator on one channel, as its OWNER sees them (ADR 0050 §4).
 *
 * `status` is tomo's standing, not Twitch's mod list: somebody Twitch still
 * has as a moderator can be `suspended` here by the owner, and `ended` means
 * Twitch no longer names them, or the owner lifted a suspension and their next
 * re-check has not run yet.
 */
export const ChannelModerator = z.object({
  uid: z.string(),
  handle: z.string().nullable(),
  display_name: z.string().nullable(),
  status: z.enum(['active', 'ended', 'suspended']),
  since: z.string().datetime(),
});
export type ChannelModerator = z.infer<typeof ChannelModerator>;

export const ChannelModeratorList = z.object({ data: z.array(ChannelModerator) });

export const ChannelModeratorPatch = z.object({ suspended: z.boolean() });
export type ChannelModeratorPatch = z.infer<typeof ChannelModeratorPatch>;
